import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { Box, Button, Card, CardContent, Chip, Stack, Typography } from '@mui/material';
import { CalendarMonth } from '@mui/icons-material';
import { MY_WEEKLY_ROUTINE } from 'api/graphql/play';
import { dayLabel, DAY_VALUES, todayIso } from 'components/play/constants';
import routes from 'common/routes';

const MAX_PER_DAY = 3;

/**
 * OTRIX Play — dashboard preview of the week's routine. Replaces the 1:1 "next sessions" list
 * for Play users: one row per weekday with its exercises (or a rest tag), today highlighted.
 */
function PlayWeekRoutinePreview() {
  const { t } = useTranslation(['play']);
  const navigate = useNavigate();
  const { data } = useQuery(MY_WEEKLY_ROUTINE, { fetchPolicy: 'cache-and-network' });

  const iso = todayIso();
  const itemsByDay = useMemo(() => {
    const map = {};
    (data?.myWeeklyRoutine?.items || []).forEach((i) => {
      (map[i.day_of_week] = map[i.day_of_week] || []).push(i);
    });
    return map;
  }, [data]);

  const hasAny = Object.keys(itemsByDay).length > 0;

  return (
    <Card sx={{ border: '1px solid', borderColor: 'hairline.main', borderRadius: '16px', bgcolor: 'surface.content', boxShadow: 'inset 0 1px 0 var(--otx-inner-highlight)' }}>
      <CardContent>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
            <CalendarMonth sx={{ color: 'gold.main', flex: 'none' }} />
            <Typography variant="subtitle1" fontWeight={800} noWrap>
              {t('dashboard.weekTitle', { ns: 'play' })}
            </Typography>
          </Stack>
          <Button
            size="small"
            onClick={() => navigate(routes.playRoutine.path)}
            sx={{ color: 'gold.main', flex: 'none', minWidth: 0 }}>
            {t('dashboard.viewRoutine', { ns: 'play' })}
          </Button>
        </Stack>

        {!hasAny ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            {t('dashboard.empty', { ns: 'play' })}
          </Typography>
        ) : (
          <Stack spacing={1}>
            {DAY_VALUES.map((d) => {
              const items = itemsByDay[d] || [];
              const isToday = d === iso;
              return (
                <Stack
                  key={d}
                  direction="row"
                  spacing={1.5}
                  alignItems="center"
                  sx={{
                    px: 1.5,
                    py: 1,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: isToday ? 'hairline.strong' : 'hairline.main',
                    bgcolor: isToday ? 'selected.bg' : 'surface.raised'
                  }}>
                  {/* La columna del día era 92px fijos e irreducibles: a 320 no dejaba espacio a
                      los nombres y se salían de la card. */}
                  <Box sx={{ width: { xs: 64, sm: 92 }, flex: 'none' }}>
                    <Typography variant="body2" fontWeight={800} noWrap sx={{ color: isToday ? 'selected.fg' : 'text.primary' }}>
                      {dayLabel(t, d)}
                    </Typography>
                    {isToday && (
                      <Typography variant="caption" sx={{ color: 'gold.main' }}>
                        {t('dashboard.today', { ns: 'play' })}
                      </Typography>
                    )}
                  </Box>
                  {items.length === 0 ? (
                    <Chip size="small" variant="outlined" label={t('dashboard.rest', { ns: 'play' })} sx={{ color: 'text.secondary' }} />
                  ) : (
                    <Stack direction="row" flexWrap="wrap" useFlexGap spacing={0.75} sx={{ minWidth: 0, flex: 1 }}>
                      {items.slice(0, MAX_PER_DAY).map((item) => (
                        <Chip
                          key={item.id}
                          size="small"
                          label={item.exercise?.localized_name}
                          // maxWidth en px duros (160) desbordaba a 320: con '100%' el Chip elipsa
                          // su propio label contra el ancho disponible.
                          sx={{ bgcolor: 'surface.overlay', maxWidth: '100%' }}
                        />
                      ))}
                      {items.length > MAX_PER_DAY && (
                        <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
                          {t('dashboard.more', { ns: 'play', count: items.length - MAX_PER_DAY })}
                        </Typography>
                      )}
                    </Stack>
                  )}
                </Stack>
              );
            })}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}

export default PlayWeekRoutinePreview;
